<template>
  <div class="contact-sync-v2">
    <div class="main-wrapper">
      <layout-header></layout-header>
      <layout-sidebar></layout-sidebar>

      <div class="page-wrapper">
        <div class="content container-fluid sync-page">
          <!-- ============================= KEPALA HALAMAN ============================= -->
          <div class="sync-page-head">
            <div>
              <span class="sync-eyebrow">
                <i class="fa fa-refresh"></i> Perbarui Kontak
              </span>
              <h3 class="sync-title">Sinkronisasi Kontak</h3>
              <p class="sync-subtitle">
                Perbarui dan cocokkan data kontak WhatsApp Anda agar selalu
                terbaru secara otomatis.
              </p>
            </div>

            <div class="sync-head-actions">
              <router-link to="/contacts-v2" class="btn sync-btn sync-btn-outline">
                <i class="fa fa-arrow-left"></i>
                <span>Kembali ke Kontak</span>
              </router-link>

              <button
                type="button"
                class="btn sync-btn sync-btn-primary"
                :disabled="syncing"
                @click="mulaiSinkronisasi"
              >
                <i :class="syncing ? 'fa fa-spinner fa-spin' : 'fa fa-refresh'"></i>
                <span>{{ syncing ? "Sedang Menyinkronkan..." : "Mulai Sinkronisasi" }}</span>
              </button>
            </div>
          </div>

          <!-- ============================= RINGKASAN STATUS ============================= -->
          <div class="sync-stats">
            <div class="sync-stat">
              <div class="sync-stat-icon is-orange">
                <i class="fa fa-history"></i>
              </div>
              <div class="sync-stat-copy">
                <span class="sync-stat-label">Sinkronisasi Terakhir</span>
                <strong>{{ tanggalSinkronisasiTerakhir }}</strong>
                <small>{{ detailSinkronisasiTerakhir }}</small>
              </div>
            </div>

            <div class="sync-stat">
              <div class="sync-stat-icon is-green">
                <i class="fa fa-users"></i>
              </div>
              <div class="sync-stat-copy">
                <span class="sync-stat-label">Total Kontak Terhubung</span>
                <strong>{{ totalKontak }}</strong>
                <small>Jumlah kontak yang tersimpan saat ini</small>
              </div>
            </div>
          </div>

          <div class="sync-layout">
            <!-- ============================= OPSI SINKRONISASI ============================= -->
            <div class="sync-card">
              <div class="sync-card-head">
                <h5>Opsi Sinkronisasi</h5>
                <p>Pilih akun WhatsApp dan grup tujuan.</p>
              </div>

              <div class="sync-card-body">
                <div v-if="errorMessage" class="sync-alert">
                  <i class="fa fa-exclamation-circle"></i>
                  <span>{{ errorMessage }}</span>
                </div>

                <div class="form-group">
                  <label>
                    Pilih Akun WhatsApp
                    <span class="sync-required">*</span>
                  </label>
                  <multiselect
                    v-model="selectedLicense"
                    id="sync-license-v2"
                    label="license_name"
                    track-by="id"
                    :placeholder="'Pilih Akun WhatsApp'"
                    :multiple="false"
                    open-direction="bottom"
                    :custom-label="customLabel"
                    :options="licenseOptions"
                    :searchable="true"
                    :loading="isLoadingLicense"
                    :internal-search="false"
                    :close-on-select="true"
                    :options-limit="300"
                    :limit="3"
                    :limit-text="limitText"
                    :max-height="600"
                    :show-no-results="true"
                    :hide-selected="true"
                    :show-labels="false"
                    :disabled="syncing"
                    @search-change="asyncFind"
                  >
                    <span slot="noResult">Tidak ada akun ditemukan.</span>
                  </multiselect>
                  <small class="sync-help">
                    Kontak akan diambil dari akun WhatsApp yang dipilih.
                  </small>

                  <div
                    v-if="selectedLicense"
                    class="sync-account-status"
                    :class="'is-' + statusKoneksi.tone"
                  >
                    <i class="fa fa-circle"></i>
                    Status akun: {{ statusKoneksi.label }}
                  </div>
                </div>

                <div class="form-group">
                  <label>Masukkan ke Grup (opsional)</label>
                  <multiselect
                    v-model="selectedGroups"
                    id="sync-group-v2"
                    label="name"
                    track-by="id"
                    :placeholder="'Pilih Grup Tujuan'"
                    :multiple="true"
                    open-direction="bottom"
                    :options="groupOptions"
                    :searchable="true"
                    :loading="loadingGroup"
                    :internal-search="true"
                    :close-on-select="false"
                    :options-limit="300"
                    :limit="3"
                    :limit-text="limitText"
                    :max-height="600"
                    :show-no-results="true"
                    :hide-selected="true"
                    :show-labels="false"
                    :disabled="syncing"
                    @search-change="onGroupSearch"
                    @select="onGroupChange"
                    @remove="onGroupChange"
                  >
                    <span slot="noResult">
                      <span v-if="!groupSearch.trim()">Tidak ada grup ditemukan.</span>
                      <button
                        v-else
                        type="button"
                        class="sync-inline-create-btn"
                        :disabled="creatingGroup || syncing"
                        @mousedown.prevent.stop
                        @click.prevent.stop="buatGrupBaru"
                      >
                        <i :class="creatingGroup ? 'fa fa-spinner fa-spin' : 'fa fa-plus'"></i>
                        <span v-if="creatingGroup">Membuat grup...</span>
                        <span v-else>Buat grup baru: "{{ groupSearch.trim() }}"</span>
                      </button>
                    </span>
                  </multiselect>
                  <div v-if="grupBaruTerdeteksi" class="sync-inline-create">
                    <button
                      type="button"
                      class="sync-inline-create-btn"
                      :disabled="creatingGroup || syncing"
                      @click.prevent="buatGrupBaru"
                    >
                      <i :class="creatingGroup ? 'fa fa-spinner fa-spin' : 'fa fa-plus'"></i>
                      <span v-if="creatingGroup">Membuat grup...</span>
                      <span v-else>Buat grup baru: "{{ groupSearch.trim() }}"</span>
                    </button>
                  </div>
                  <div v-if="groupCreateError" class="sync-inline-error">
                    {{ groupCreateError }}
                  </div>
                  <small class="sync-help">
                    Kosongkan jika kontak tidak perlu dimasukkan ke grup.
                  </small>
                </div>

                <div class="form-group">
                  <label>Tagging Massal (opsional)</label>
                  <multiselect
                    v-model="selectedTags"
                    id="sync-tag-v2"
                    label="name"
                    track-by="id"
                    :placeholder="'Pilih Tagging'"
                    :multiple="true"
                    open-direction="bottom"
                    :options="tagOptions"
                    :searchable="true"
                    :loading="loadingTag"
                    :internal-search="true"
                    :close-on-select="false"
                    :options-limit="300"
                    :limit="3"
                    :limit-text="limitText"
                    :max-height="600"
                    :show-no-results="true"
                    :hide-selected="true"
                    :show-labels="false"
                    :disabled="syncing"
                    @search-change="onTagSearch"
                    @select="onTagChange"
                    @remove="onTagChange"
                  >
                    <span slot="noResult">
                      <span v-if="!tagSearch.trim()">Tidak ada tag ditemukan.</span>
                      <button
                        v-else
                        type="button"
                        class="sync-inline-create-btn"
                        :disabled="creatingTag || syncing"
                        @mousedown.prevent.stop
                        @click.prevent.stop="buatTagBaru"
                      >
                        <i :class="creatingTag ? 'fa fa-spinner fa-spin' : 'fa fa-plus'"></i>
                        <span v-if="creatingTag">Membuat tagging...</span>
                        <span v-else>Buat tagging baru: "{{ tagSearch.trim() }}"</span>
                      </button>
                    </span>
                  </multiselect>
                  <div v-if="tagBaruTerdeteksi" class="sync-inline-create">
                    <button
                      type="button"
                      class="sync-inline-create-btn"
                      :disabled="creatingTag || syncing"
                      @click.prevent="buatTagBaru"
                    >
                      <i :class="creatingTag ? 'fa fa-spinner fa-spin' : 'fa fa-plus'"></i>
                      <span v-if="creatingTag">Membuat tagging...</span>
                      <span v-else>Buat tagging baru: "{{ tagSearch.trim() }}"</span>
                    </button>
                  </div>
                  <div v-if="tagCreateError" class="sync-inline-error">
                    {{ tagCreateError }}
                  </div>
                  <small class="sync-help">
                    Tagging hanya diterapkan ke kontak baru pada grup tujuan.
                    Penyesuaian per kontak dapat dilakukan dari daftar Kontak.
                  </small>
                </div>

                <div class="sync-note">
                  <i class="fa fa-info-circle"></i>
                  <span>
                    Kontak yang sudah tersimpan tidak akan dibuat dua kali.
                  </span>
                </div>
              </div>
            </div>

            <!-- ============================= STATUS SINKRONISASI ============================= -->
            <div class="sync-card">
              <div class="sync-card-head">
                <h5>Status Sinkronisasi</h5>
                <p>Informasi proses yang sedang berjalan atau terakhir dilakukan.</p>
              </div>

              <div class="sync-card-body">
                <div v-if="syncing" class="sync-loading-box">
                  <i class="fa fa-spinner fa-spin"></i>
                  <div>
                    <strong>Sedang menyinkronkan...</strong>
                    <p>
                      Proses sinkronisasi sedang berjalan di latar belakang.
                      Anda dapat meninggalkan halaman ini. Notifikasi akan
                      dikirimkan ke WhatsApp Anda setelah proses selesai.
                    </p>
                  </div>
                </div>

                <div
                  v-else-if="statusMessage"
                  class="sync-status-message"
                  :class="{ 'is-success': !errorMessage, 'is-error': !!errorMessage }"
                >
                  <i
                    :class="
                      errorMessage
                        ? 'fa fa-exclamation-circle'
                        : 'fa fa-check-circle'
                    "
                  ></i>
                  <span>{{ statusMessage }}</span>
                </div>

                <div v-else class="sync-idle">
                  <i class="fa fa-refresh"></i>
                  <span>Siap memulai sinkronisasi kontak.</span>
                </div>
              </div>
            </div>
          </div>

          <!-- ============================= RIWAYAT ============================= -->
          <div class="sync-card sync-history-card">
            <div class="sync-card-head sync-history-head">
              <div>
                <h5>Riwayat Sinkronisasi</h5>
                <p>Catatan sinkronisasi yang tersimpan di perangkat ini.</p>
              </div>
              <span class="sync-history-badge">{{ riwayat.length }} Catatan</span>
            </div>

            <div class="table-responsive">
              <table class="table sync-table mb-0">
                <thead>
                  <tr>
                    <th>Tanggal Sinkronisasi</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-if="riwayat.length">
                    <tr v-for="item in riwayat" :key="item.id">
                      <td>{{ formatTanggal(item.tanggal) }}</td>
                      <td>
                        <span
                          class="sync-history-status"
                          :class="'is-' + statusKelas(item.status)"
                        >
                          {{ item.status }}
                        </span>
                      </td>
                    </tr>
                  </template>

                  <template v-else>
                    <tr>
                      <td colspan="2">
                        <div class="sync-empty">
                          <div class="sync-empty-icon">
                            <i class="fa fa-history"></i>
                          </div>
                          <h6>Belum ada riwayat sinkronisasi</h6>
                          <p>
                            Riwayat akan muncul setelah Anda menjalankan
                            sinkronisasi pada perangkat ini.
                          </p>
                        </div>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
const qs = require("qs");

import LayoutHeader from "@/components/layouts/Header.vue";
import LayoutSidebar from "@/components/layouts/Sidebar.vue";

import Multiselect from "vue-multiselect";
require("vue-multiselect/dist/vue-multiselect.min.css");

import { APICoreV2 } from "@/utils/servicesApi";
import { getXAuthToken, getXAuthAgent } from "@/utils/auth";
import {
  ambilKontakGrup,
  terapkanTagKeKontakBaru
} from "@/utils/contactTagging";

const STORAGE_KEY = "waajo_contact_sync_history_v2";

export default {
  name: "ContactSyncV2",

  components: {
    LayoutHeader,
    LayoutSidebar,
    Multiselect
  },

  data() {
    return {
      // Akun WhatsApp / sumber kontak
      selectedLicense: null,
      licenseOptions: [],
      isLoadingLicense: false,
      licenseParam: {
        license_name: "",
        license_phone_number: "",
        license_status: "",
        page: 0,
        perPage: 20
      },

      // Grup tujuan
      selectedGroups: [],
      groupOptions: [],
      loadingGroup: false,
      groupSearch: "",
      creatingGroup: false,
      groupCreateError: "",
      groupParam: {
        name: "",
        page: 0,
        perPage: 1000
      },

      // Tagging massal
      selectedTags: [],
      tagOptions: [],
      loadingTag: false,
      tagSearch: "",
      creatingTag: false,
      tagCreateError: "",
      activeGroupIds: [],
      activeTagIds: [],
      baselineKontakIds: [],
      taggingWarning: "",

      // Ringkasan
      totalKontak: 0,

      // Status proses
      syncing: false,
      checkingStatus: false,
      statusMessage: "",
      errorMessage: "",
      activeParams: null,
      pollTimer: null,

      // Riwayat proses yang tersimpan di perangkat
      riwayat: []
    };
  },

  computed: {
    statusKoneksi() {
      if (!this.selectedLicense) {
        return {
          label: "Belum dipilih",
          tone: "neutral",
          detail: "Pilih akun WhatsApp untuk melihat status."
        };
      }

      const license = this.selectedLicense;
      let online = null;

      if (
        license.is_online_status !== undefined &&
        license.is_online_status !== null
      ) {
        online =
          license.is_online_status == 1 ||
          license.is_online_status === "1";
      } else if (license.license_status) {
        online = license.license_status === "active";
      }

      if (online === true) {
        return {
          label: "Terhubung",
          tone: "success",
          detail: license.license_phone_number || license.license_name
        };
      }

      if (online === false) {
        return {
          label: "Tidak terhubung",
          tone: "danger",
          detail: license.license_phone_number || license.license_name
        };
      }

      return {
        label: "Status belum diketahui",
        tone: "neutral",
        detail: license.license_phone_number || license.license_name
      };
    },

    riwayatTerakhirSukses() {
      return (
        this.riwayat.find(item => {
          return item.status === "Selesai";
        }) || null
      );
    },

    tanggalSinkronisasiTerakhir() {
      if (!this.riwayatTerakhirSukses) {
        return "Belum pernah";
      }

      return this.formatTanggal(this.riwayatTerakhirSukses.tanggal);
    },

    detailSinkronisasiTerakhir() {
      if (!this.riwayatTerakhirSukses) {
        return "Mulai sinkronisasi pertama Anda";
      }

      return "Berdasarkan catatan di perangkat ini";
    },
    grupBaruTerdeteksi() {
      const query = String(this.groupSearch || "").trim().toLowerCase();
      if (!query) {
        return false;
      }
      return !this.groupOptions.some(grup => {
        return String(grup.name || "").trim().toLowerCase() === query;
      });
    },
    tagBaruTerdeteksi() {
      const query = String(this.tagSearch || "").trim().toLowerCase();
      if (!query) {
        return false;
      }
      return !this.tagOptions.some(tag => {
        return String(tag.name || "").trim().toLowerCase() === query;
      });
    }
  },

  mounted() {
    this.muatRiwayat();
    this.muatLisensi();
    this.muatGrup();
    this.muatTag();
    this.muatTotalKontak();
  },

  beforeDestroy() {
    this.hentikanPemantauan();
  },

  methods: {
    /* ============================= PEMUATAN DATA ============================= */

    async muatLisensi() {
      this.isLoadingLicense = true;

      try {
        const response = await this.$store.dispatch(
          "license/getLicenseList",
          this.licenseParam
        );

        this.licenseOptions =
          (response && response.data && response.data.data) || [];
      } catch (error) {
        console.log(error);
        this.licenseOptions = [];
      } finally {
        this.isLoadingLicense = false;
      }
    },

    asyncFind(query) {
      this.licenseParam.license_name = query;
      this.muatLisensi();
    },

    async muatGrup() {
      this.loadingGroup = true;

      try {
        const response = await this.$store.dispatch("contact/getGroupList", {
          name: "",
          page: 0,
          perPage: 1000
        });

        this.groupOptions = (response && response.data) || [];

        const groupId = this.$route.query.group_id;

        if (groupId) {
          const found = this.groupOptions.find(group => {
            return String(group.id) === String(groupId);
          });

          if (found) {
            this.selectedGroups = [found];
          }
        }
      } catch (error) {
        console.log(error);
        this.groupOptions = [];
      } finally {
        this.loadingGroup = false;
      }
    },

    async muatTag() {
      this.loadingTag = true;

      try {
        const response = await this.$store.dispatch("contact/getTagList", {
          name: "",
          page: 0,
          perPage: 1000
        });
        const payload = (response && response.data) || {};
        const daftar = Array.isArray(payload.data) ? payload.data : [];

        this.tagOptions = daftar.map(tag => {
          return {
            id: tag.id,
            name: this.namaTagBersih(tag.name)
          };
        });
      } catch (error) {
        console.log(error);
        this.tagOptions = [];
      } finally {
        this.loadingTag = false;
      }
    },

    onGroupSearch(query) {
      this.groupSearch = query || "";
      this.groupCreateError = "";
    },

    onTagSearch(query) {
      this.tagSearch = query || "";
      this.tagCreateError = "";
    },

    onGroupChange() {
      this.groupSearch = "";
      this.groupCreateError = "";
    },

    onTagChange() {
      this.tagSearch = "";
      this.tagCreateError = "";
    },

    tambahPilihanGrup(idGrup) {
      const sudahAda = this.selectedGroups.some(grup => {
        return String(grup.id) === String(idGrup);
      });

      if (!sudahAda) {
        const grup = this.groupOptions.find(item => {
          return String(item.id) === String(idGrup);
        });

        if (grup) {
          this.selectedGroups.push(grup);
        }
      }
    },

    tambahPilihanTag(idTag) {
      const sudahAda = this.selectedTags.some(tag => {
        return String(tag.id) === String(idTag);
      });

      if (!sudahAda) {
        const tag = this.tagOptions.find(item => {
          return String(item.id) === String(idTag);
        });

        if (tag) {
          this.selectedTags.push(tag);
        }
      }
    },

    normalisasiGrupBaru(response, namaCadangan) {
      const payload =
        response && response.data !== undefined ? response.data : response;
      const grup = Array.isArray(payload) ? payload[0] : payload;

      if (!grup) {
        return null;
      }

      return {
        ...grup,
        id: grup.id !== undefined ? grup.id : grup.Id,
        name: grup.name || grup.group_name || namaCadangan
      };
    },

    normalisasiTagBaru(response, namaCadangan) {
      const payload =
        response && response.data !== undefined ? response.data : response;
      const tag = Array.isArray(payload) ? payload[0] : payload;

      if (!tag) {
        return null;
      }

      return {
        ...tag,
        id: tag.id !== undefined ? tag.id : tag.Id,
        name: this.namaTagBersih(tag.name || namaCadangan)
      };
    },

    async buatGrupBaru() {
      const nama = String(this.groupSearch || "").trim();

      if (!nama || this.creatingGroup || this.syncing) {
        return;
      }

      const grupAda = this.groupOptions.find(grup => {
        return String(grup.name || "").trim().toLowerCase() === nama.toLowerCase();
      });

      if (grupAda) {
        this.tambahPilihanGrup(grupAda.id);
        this.groupSearch = "";
        return;
      }

      this.creatingGroup = true;
      this.groupCreateError = "";

      try {
        const response = await this.$store.dispatch("contact/createGroup", {
          id: "",
          name: nama,
          description: ""
        });
        const grup = this.normalisasiGrupBaru(response, nama);

        if (!grup || grup.id === undefined || grup.id === null || grup.id === "") {
          throw new Error("ID grup baru tidak ditemukan pada respons server.");
        }

        const sudahAda = this.groupOptions.some(item => {
          return String(item.id) === String(grup.id);
        });

        if (!sudahAda) {
          this.groupOptions.push(grup);
        }

        this.tambahPilihanGrup(grup.id);
        this.groupSearch = "";
      } catch (error) {
        console.log(error);
        this.groupCreateError =
          "Gagal membuat grup baru. Silakan periksa nama grup lalu coba lagi.";
      } finally {
        this.creatingGroup = false;
      }
    },

    async buatTagBaru() {
      const nama = String(this.tagSearch || "").trim();

      if (!nama || this.creatingTag || this.syncing) {
        return;
      }

      const tagAda = this.tagOptions.find(tag => {
        return String(tag.name || "").trim().toLowerCase() === nama.toLowerCase();
      });

      if (tagAda) {
        this.tambahPilihanTag(tagAda.id);
        this.tagSearch = "";
        return;
      }

      this.creatingTag = true;
      this.tagCreateError = "";

      try {
        const response = await this.$store.dispatch("crm/createTag", {
          id: "",
          name: nama,
          description: ""
        });
        const tag = this.normalisasiTagBaru(response, nama);

        if (!tag || tag.id === undefined || tag.id === null || tag.id === "") {
          throw new Error("ID tagging baru tidak ditemukan pada respons server.");
        }

        const sudahAda = this.tagOptions.some(item => {
          return String(item.id) === String(tag.id);
        });

        if (!sudahAda) {
          this.tagOptions.push(tag);
        }

        this.tambahPilihanTag(tag.id);
        this.tagSearch = "";
      } catch (error) {
        console.log(error);
        this.tagCreateError =
          "Gagal membuat tagging baru. Silakan periksa nama tagging lalu coba lagi.";
      } finally {
        this.creatingTag = false;
      }
    },

    namaTagBersih(nama) {
      return String(nama || "").replace(/\s~\s\d+$/, "");
    },

    async muatTotalKontak() {
      try {
        const response = await this.$store.dispatch(
          "contact/getContactList",
          {
            keywords: "",
            phone_number: "",
            email: "",
            name: "",
            group_id: [],
            tag_id: [],
            page: 0,
            perPage: 1
          }
        );

        this.totalKontak = (response && response.totalRecords) || 0;
      } catch (error) {
        console.log(error);
        this.totalKontak = 0;
      }
    },

    /* ============================= PROSES SINKRONISASI ============================= */

    bangunParamSinkronisasi() {
      let listId = "";

      this.selectedGroups.forEach(group => {
        listId += group.id + ",";
      });

      return {
        license_id: this.selectedLicense ? this.selectedLicense.id : "",
        list_id: listId
      };
    },

    async mulaiSinkronisasi() {
      if (this.syncing) {
        return;
      }

      this.errorMessage = "";
      this.taggingWarning = "";

      if (!this.selectedLicense) {
        this.errorMessage = "Silakan pilih akun WhatsApp terlebih dahulu.";
        return;
      }

      if (this.selectedTags.length && !this.selectedGroups.length) {
        this.errorMessage =
          "Pilih grup tujuan terlebih dahulu agar tagging massal dapat diterapkan.";
        return;
      }

      const params = this.bangunParamSinkronisasi();
      this.activeGroupIds = this.selectedGroups.map(group => group.id);
      this.activeTagIds = this.selectedTags.map(tag => tag.id);
      this.baselineKontakIds = [];

      if (this.activeTagIds.length) {
        try {
          const kontakAwal = await ambilKontakGrup(
            this.$store,
            this.activeGroupIds
          );
          this.baselineKontakIds = kontakAwal.map(kontak => kontak.id);
        } catch (error) {
          console.log(error);
          this.taggingWarning =
            "Tagging massal tidak dapat disiapkan. Sinkronisasi tetap dapat dijalankan, lalu tagging diatur dari daftar Kontak.";
          this.activeTagIds = [];
        }
      }

      this.syncing = true;
      this.statusMessage =
        "Proses sinkronisasi sedang berjalan di latar belakang. Anda dapat meninggalkan halaman ini. Notifikasi akan dikirimkan ke WhatsApp Anda setelah proses selesai.";
      this.activeParams = params;

      try {
        await this.$store.dispatch("contact/syncContactsCheck", params);
      } catch (error) {
        console.log(error);
        this.syncing = false;
        this.statusMessage = "Sinkronisasi belum berhasil. Silakan coba lagi.";
        this.errorMessage = this.pesanSinkronisasiGagal(error);
        this.catatRiwayat("Gagal");
        return;
      }

      try {
        await this.$store.dispatch("contact/syncContactsProcess", params);

        this.$fire({
          title: "Sinkronisasi Dimulai",
          text:
            "Proses sinkronisasi sedang berjalan di latar belakang. Anda dapat meninggalkan halaman ini. Notifikasi akan dikirimkan ke WhatsApp Anda setelah proses selesai.",
          type: "info",
          timer: 5000
        });

        this.jadwalkanPemantauan();
      } catch (error) {
        console.log(error);
        this.syncing = false;
        this.statusMessage = "Sinkronisasi belum berhasil. Silakan coba lagi.";
        this.errorMessage = this.pesanSinkronisasiGagal(error);
        this.catatRiwayat("Gagal");
      }
    },

    /* ============================= PEMANTAUAN STATUS ============================= */

    jadwalkanPemantauan() {
      this.hentikanPemantauan();

      this.pollTimer = setInterval(() => {
        this.periksaStatusSinkronisasi();
      }, 5000);
    },

    hentikanPemantauan() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    async periksaStatusSinkronisasi() {
      if (!this.syncing || !this.activeParams || this.checkingStatus) {
        return;
      }

      this.checkingStatus = true;

      try {
        const hasil = await this.cekStatusPending(this.activeParams);

        if (hasil.selesai) {
          this.selesaikanSinkronisasi();
        } else if (
          !hasil.pending &&
          hasil.pesan &&
          /not found|required|invalid|tidak ditemukan|wajib/i.test(hasil.pesan)
        ) {
          this.gagalkanSinkronisasi(hasil.pesan);
        }
      } catch (error) {
        // Kegagalan jaringan tidak langsung dianggap gagal; pemantauan berlanjut.
        console.log(error);
      } finally {
        this.checkingStatus = false;
      }
    },

    cekStatusPending(params) {
      const url =
        process.env.VUE_APP_BASE_API +
        APICoreV2() +
        "/contact/check-sync-contacts";

      const headers = {
        "Content-Type": "application/x-www-form-urlencoded"
      };
      const token = getXAuthToken();
      const agent = getXAuthAgent();

      if (token) {
        headers["X-Auth-Token"] = "Bearer " + token;
      }

      if (agent) {
        headers["X-Auth-Agent"] = "Bearer " + agent;
      }

      return axios
        .post(url, qs.stringify(params), {
          headers: headers,
          timeout: 20000,
          validateStatus: () => true
        })
        .then(response => {
          const body = response.data || {};

          if (Number(body.status) === 200) {
            return {
              selesai: true,
              pending: false,
              pesan: body.msg || ""
            };
          }

          const pesan = String(body.msg || "");
          const pending = /sedang ada proses sinkronisasi|sync is on the way/i.test(
            pesan
          );

          return {
            selesai: false,
            pending: pending,
            pesan: pesan
          };
        });
    },

    async selesaikanSinkronisasi() {
      this.hentikanPemantauan();
      this.syncing = false;

      let pesanTambahan = "";

      if (this.activeTagIds.length && this.activeGroupIds.length) {
        this.statusMessage = "Sinkronisasi selesai. Menerapkan tagging...";

        try {
          const hasilTag = await terapkanTagKeKontakBaru(this.$store, {
            groupIds: this.activeGroupIds,
            tagIds: this.activeTagIds,
            baselineIds: this.baselineKontakIds
          });

          if (hasilTag.ok) {
            pesanTambahan =
              " Tagging diterapkan ke " + hasilTag.count + " kontak baru.";

            if (hasilTag.gagal > 0) {
              this.taggingWarning = hasilTag.message;
            }
          } else {
            this.taggingWarning = hasilTag.message;
          }
        } catch (tagError) {
          console.log(tagError);
          this.taggingWarning =
            "Tagging massal belum dapat diterapkan. Silakan atur tagging dari daftar Kontak.";
        }
      }

      this.statusMessage =
        "Sinkronisasi kontak berhasil diselesaikan." + pesanTambahan;
      this.catatRiwayat("Selesai");
      this.muatTotalKontak();

      const teks = this.taggingWarning
        ? "Sinkronisasi kontak berhasil diselesaikan. " + this.taggingWarning
        : "Sinkronisasi kontak berhasil diselesaikan." + pesanTambahan;

      this.$fire({
        title: this.taggingWarning
          ? "Sinkronisasi Selesai dengan Catatan"
          : "Sinkronisasi Selesai",
        text: teks,
        type: this.taggingWarning ? "warning" : "success",
        timer: this.taggingWarning ? 6000 : 4000
      });
    },

    gagalkanSinkronisasi(pesan) {
      this.hentikanPemantauan();
      this.syncing = false;
      this.statusMessage = "Sinkronisasi belum berhasil. Silakan coba lagi.";
      this.errorMessage = this.pesanSinkronisasiGagal({ message: pesan });
      this.catatRiwayat("Gagal");

      this.$fire({
        title: "Sinkronisasi Belum Berhasil",
        text: "Sinkronisasi belum berhasil. Silakan coba lagi.",
        type: "error",
        timer: 4000
      });
    },

    /* ============================= RIWAYAT ============================= */

    muatRiwayat() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);

        if (!raw) {
          this.riwayat = [];
          return;
        }

        const parsed = JSON.parse(raw);
        const catatan = Array.isArray(parsed) ? parsed : [];

        this.riwayat = catatan.filter(item => {
          return item && (item.status === "Selesai" || item.status === "Gagal");
        });
      } catch (error) {
        console.log(error);
        this.riwayat = [];
      }
    },

    simpanRiwayat() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.riwayat));
      } catch (error) {
        console.log(error);
      }
    },

    catatRiwayat(status) {
      const entri = {
        id: String(Date.now()),
        tanggal: new Date().toISOString(),
        status: status
      };

      this.riwayat.unshift(entri);
      this.riwayat = this.riwayat.slice(0, 10);
      this.simpanRiwayat();
    },

    /* ============================= UTILITAS ============================= */

    customLabel({ license_name, license_phone_number }) {
      return license_name + " – " + license_phone_number;
    },

    limitText(count) {
      return "dan " + count + " lainnya";
    },

    formatTanggal(value) {
      if (!value) {
        return "-";
      }

      return this.$moment(value).format("DD MMM YYYY HH:mm");
    },

    statusKelas(status) {
      if (status === "Selesai") {
        return "success";
      }

      if (status === "Gagal") {
        return "danger";
      }

      return "process";
    },

    ambilPesanError(error) {
      if (!error) {
        return "";
      }

      let pesan = "";

      if (error.message) {
        pesan = String(error.message);
      } else if (
        error.response &&
        error.response.data &&
        error.response.data.msg
      ) {
        pesan = String(error.response.data.msg);
      }

      return pesan
        .replace(/<br\s*\/?>/gi, " ")
        .replace(/<[^>]*>/g, "")
        .trim();
    },

    pesanSinkronisasiGagal(error) {
      const pesanDasar =
        "Gagal memproses data. Silakan periksa koneksi WhatsApp Anda, lalu coba lagi.";
      const detail = this.ambilPesanError(error);
      const teks = detail.toLowerCase();

      if (teks.indexOf("sedang ada proses sinkronisasi") !== -1) {
        return detail;
      }

      if (teks.indexOf("sync is on the way") !== -1) {
        return "Masih ada proses sinkronisasi yang berjalan. Mohon tunggu hingga proses sebelumnya selesai.";
      }

      if (teks.indexOf("group id") !== -1 && teks.indexOf("not found") !== -1) {
        return "Grup tujuan tidak ditemukan. Silakan pilih grup lain.";
      }

      if (teks.indexOf("license") !== -1 && teks.indexOf("not found") !== -1) {
        return "Akun WhatsApp tidak ditemukan. Silakan pilih akun lain.";
      }

      if (
        !detail ||
        teks.indexOf("network error") !== -1 ||
        teks.indexOf("timeout") !== -1 ||
        teks.indexOf("failed to fetch") !== -1 ||
        teks.indexOf("dial tcp") !== -1 ||
        teks.indexOf("runtime error") !== -1 ||
        teks.indexOf("panic") !== -1 ||
        teks.indexOf("sql") !== -1
      ) {
        return pesanDasar;
      }

      return pesanDasar;
    }
  }
};
</script>

<style scoped>
.contact-sync-v2 {
  color: #1f2a44;
}

.sync-page {
  padding-top: 24px;
  padding-bottom: 60px;
}

/* ============================= KEPALA HALAMAN ============================= */

.sync-page-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;
}

.sync-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 7px;
  color: #f39c12;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.sync-title {
  margin: 0;
  color: #101828;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.sync-title span {
  color: #f39c12;
  font-size: 18px;
  font-weight: 600;
}

.sync-subtitle {
  margin: 6px 0 0;
  color: #667085;
  font-size: 13px;
}

.sync-head-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding-top: 4px;
}

/* ============================= TOMBOL ============================= */

.sync-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 40px;
  padding: 0 18px;
  border: 1px solid transparent;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  transition: background-color 0.18s ease, border-color 0.18s ease,
    box-shadow 0.18s ease;
}

.sync-btn:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(243, 156, 18, 0.22);
}

.sync-btn:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.sync-btn-primary {
  color: #ffffff;
  background-color: #f39c12;
  border-color: #f39c12;
  font-weight: 700;
  box-shadow: 0 7px 16px rgba(243, 156, 18, 0.24);
}

.sync-btn-primary:hover:not(:disabled),
.sync-btn-primary:focus:not(:disabled) {
  color: #ffffff;
  background-color: #e08e0b;
  border-color: #e08e0b;
}

.sync-btn-outline {
  color: #555555;
  background-color: #ffffff;
  border-color: #d1d5db;
}

.sync-btn-outline:hover:not(:disabled),
.sync-btn-outline:focus:not(:disabled) {
  color: #444444;
  background-color: #f9fafb;
  border-color: #b9bec7;
}

/* ============================= RINGKASAN ============================= */

.sync-stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 16px;
}

.sync-stat {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 15px 16px;
  background: #ffffff;
  border: 1px solid #e7edf5;
  border-radius: 14px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.035);
}

.sync-stat-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  font-size: 17px;
}

.sync-stat-icon.is-orange {
  color: #f39c12;
  background: #fff7e8;
}

.sync-stat-icon.is-green {
  color: #059669;
  background: #ecfdf5;
}

.sync-stat-copy {
  min-width: 0;
}

.sync-stat-label {
  display: block;
  margin-bottom: 2px;
  color: #667085;
  font-size: 12px;
  font-weight: 500;
}

.sync-stat-copy strong {
  display: block;
  overflow: hidden;
  color: #101828;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sync-stat-copy small {
  display: block;
  overflow: hidden;
  margin-top: 2px;
  color: #98a2b3;
  font-size: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ============================= KARTU ============================= */

.sync-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);
  gap: 16px;
  margin-bottom: 16px;
}

.sync-card {
  background: #ffffff;
  border: 1px solid #e7edf5;
  border-radius: 14px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
}

.sync-card-head {
  padding: 16px 18px;
  border-bottom: 1px solid #eef2f7;
}

.sync-card-head h5 {
  margin: 0;
  color: #101828;
  font-size: 15px;
  font-weight: 700;
}

.sync-card-head p {
  margin: 4px 0 0;
  color: #667085;
  font-size: 11px;
}

.sync-card-body {
  padding: 18px;
}

.sync-alert {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  margin-bottom: 14px;
  color: #912018;
  background: #fef3f2;
  border: 1px solid #fecdca;
  border-radius: 10px;
  font-size: 12px;
  line-height: 1.5;
}

.sync-alert i {
  margin-top: 2px;
  color: #d92d20;
}

.sync-required {
  color: #d92d20;
}

.sync-card .form-group {
  margin-bottom: 16px;
}

.sync-card .form-group > label {
  display: block;
  margin-bottom: 6px;
  color: #344054;
  font-size: 12px;
  font-weight: 600;
}

.sync-help {
  display: block;
  margin-top: 5px;
  color: #98a2b3;
  font-size: 10px;
  line-height: 1.5;
}

.sync-inline-create {
  margin-top: 7px;
}

.sync-inline-create-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 30px;
  padding: 5px 11px;
  color: #b36b00;
  background: #fffaf1;
  border: 1px dashed #f6c26b;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.3;
  text-align: left;
  transition: background-color 0.18s ease, border-color 0.18s ease;
}

.sync-inline-create-btn:hover:not(:disabled) {
  background: #fff3df;
  border-color: #f39c12;
}

.sync-inline-create-btn:disabled {
  cursor: wait;
  opacity: 0.65;
}

.sync-inline-error {
  margin-top: 5px;
  color: #b42318;
  font-size: 11px;
  line-height: 1.45;
}

.sync-note {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  padding: 10px 12px;
  color: #667085;
  background: #fffdf8;
  border: 1px solid #f2d29a;
  border-radius: 10px;
  font-size: 10px;
  line-height: 1.6;
}

.sync-note i {
  margin-top: 2px;
  color: #f39c12;
}

/* ============================= STATUS ============================= */

.sync-account-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 9px;
  margin-top: 8px;
  border: 1px solid #e7edf5;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 600;
}

.sync-account-status i {
  font-size: 7px;
}

.sync-account-status.is-success {
  color: #067647;
  background: #ecfdf3;
  border-color: #abefc6;
}

.sync-account-status.is-danger {
  color: #b42318;
  background: #fef3f2;
  border-color: #fecdca;
}

.sync-account-status.is-neutral {
  color: #667085;
  background: #f8fafc;
  border-color: #e2e8f0;
}

.sync-loading-box {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: #fffdf8;
  border: 1px solid #f2d29a;
  border-radius: 12px;
}

.sync-loading-box > i {
  margin-top: 2px;
  color: #f39c12;
  font-size: 22px;
}

.sync-loading-box strong {
  display: block;
  margin-bottom: 4px;
  color: #101828;
  font-size: 13px;
}

.sync-loading-box p {
  margin: 0;
  color: #667085;
  font-size: 11px;
  line-height: 1.7;
}

.sync-loading-box small {
  display: block;
  margin-top: 6px;
  color: #b36b00;
  font-size: 10px;
  font-weight: 600;
}

.sync-status-message {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid transparent;
  border-radius: 12px;
  font-size: 12px;
  line-height: 1.6;
}

.sync-status-message.is-success {
  color: #067647;
  background: #ecfdf3;
  border-color: #abefc6;
}

.sync-status-message.is-error {
  color: #912018;
  background: #fef3f2;
  border-color: #fecdca;
}

.sync-status-message i {
  margin-top: 2px;
}

.sync-idle {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px;
  color: #667085;
  background: #f8fafc;
  border: 1px dashed #e2e8f2;
  border-radius: 12px;
  font-size: 12px;
}

.sync-idle i {
  color: #f39c12;
}

/* ============================= RIWAYAT ============================= */

.sync-history-card {
  overflow: hidden;
}

.sync-history-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.sync-history-badge {
  flex: 0 0 auto;
  padding: 4px 9px;
  color: #b36b00;
  background: #fff7e8;
  border: 1px solid #f6d49b;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 600;
}

.sync-table {
  margin-bottom: 0;
}

.sync-table thead th {
  padding: 11px 14px;
  color: #667085;
  background: #f8fafc;
  border-top: 0;
  border-bottom: 1px solid #e9eef6;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
}

.sync-table tbody td {
  padding: 12px 14px;
  color: #344054;
  border-top: 1px solid #f0f3f8;
  font-size: 12px;
  vertical-align: middle;
}

.sync-history-status {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border: 1px solid transparent;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 600;
}

.sync-history-status.is-process {
  color: #b36b00;
  background: #fff7e8;
  border-color: #f6d49b;
}

.sync-history-status.is-success {
  color: #067647;
  background: #ecfdf3;
  border-color: #abefc6;
}

.sync-history-status.is-danger {
  color: #b42318;
  background: #fef3f2;
  border-color: #fecdca;
}

.sync-empty {
  padding: 34px 20px;
  text-align: center;
}

.sync-empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  margin: 0 auto 10px;
  color: #f39c12;
  background: #fff7e8;
  border-radius: 15px;
  font-size: 20px;
}

.sync-empty h6 {
  margin: 0 0 4px;
  color: #101828;
  font-size: 14px;
  font-weight: 700;
}

.sync-empty p {
  margin: 0;
  color: #667085;
  font-size: 11px;
}

/* ============================= RESPONSIF ============================= */

@media (max-width: 991px) {
  .sync-page-head {
    flex-direction: column;
  }

  .sync-head-actions {
    width: 100%;
  }

  .sync-head-actions .sync-btn {
    flex: 1;
  }

  .sync-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .sync-page {
    padding-top: 18px;
  }

  .sync-title {
    font-size: 22px;
  }

  .sync-stats {
    grid-template-columns: 1fr;
  }

  .sync-history-head {
    flex-direction: column;
  }
}
</style>

<style>
/* Penyesuaian vue-multiselect agar sesuai desain halaman sync v2. */
.contact-sync-v2 .multiselect {
  min-height: 40px;
  color: #344054;
  font-size: 13px;
}

.contact-sync-v2 .multiselect__tags {
  min-height: 40px;
  padding: 6px 36px 0 10px;
  background: #ffffff;
  border: 1px solid #e2e8f2;
  border-radius: 10px;
}

.contact-sync-v2 .multiselect__tags:focus-within {
  border-color: #f6c26b;
  box-shadow: 0 0 0 3px rgba(243, 156, 18, 0.14);
}

.contact-sync-v2 .multiselect__placeholder {
  margin-bottom: 4px;
  padding-top: 2px;
  color: #98a2b3;
}

.contact-sync-v2 .multiselect__input,
.contact-sync-v2 .multiselect__single {
  margin-bottom: 4px;
  padding: 0;
  color: #344054;
  font-size: 13px;
  line-height: 20px;
  background: transparent;
}

.contact-sync-v2 .multiselect__tag {
  margin-bottom: 4px;
  color: #b36b00;
  background: #fff7e8;
  border: 1px solid #f6d49b;
  border-radius: 20px;
}

.contact-sync-v2 .multiselect__tag-icon:after {
  color: #b36b00;
}

.contact-sync-v2 .multiselect__tag-icon:hover {
  background: #f39c12;
}

.contact-sync-v2 .multiselect__content-wrapper {
  margin-top: 4px;
  border: 1px solid #e7edf5;
  border-radius: 10px;
  box-shadow: 0 18px 44px rgba(15, 23, 42, 0.14);
}

.contact-sync-v2 .multiselect__option--highlight {
  color: #ffffff;
  background: #f39c12;
}

.contact-sync-v2 .multiselect__option--highlight:after {
  color: #ffffff;
  background: #e08e0b;
}

.contact-sync-v2 .multiselect__spinner {
  background: #ffffff;
}
</style>



// WEBPACK FOOTER //
// src/views/contacts/ContactSyncV2.vue